"use client";

import type { Carrier, Cart, PaymentOption } from "@workspace/ui/types";
import type { Dispatch } from "react";
import React, { createContext, useContext, useMemo, useReducer } from "react";

import {
  CarriersAPI,
  CartAPI,
  CreateOrderAPI,
  PaymentsAPI,
  PickupPointsAPI,
  SetAddressCheckoutAPI,
  SetCarrierCheckoutAPI,
} from "@/const/endPoint";
import { camelToSnakeCaseObject, snakeToCamelCaseObject } from "@/utils";
import axiosInstance from "@/utils/api/fetchData/axiosInstance";

export enum CartActionType {
  // Cart
  START_CART = "START_CART",
  SUCCESS_CART = "SUCCESS_CART",
  FAILURE_CART = "FAILURE_CART",

  // Update Cart
  START_UPDATE_CART = "START_UPDATE_CART",
  SUCCESS_UPDATE_CART = "SUCCESS_UPDATE_CART",
  FAILURE_UPDATE_CART = "FAILURE_UPDATE_CART",
  TOGGLE_UPDATE_CART_MODAL = "TOGGLE_UPDATE_CART_MODAL",

  // Modify voucher
  START_MODIFY_VOUCHER = "START_MODIFY_VOUCHER",
  SUCCESS_MODIFY_VOUCHER = "SUCCESS_MODIFY_VOUCHER",
  FAILURE_MODIFY_VOUCHER = "FAILURE_MODIFY_VOUCHER",

  // Clear errors
  CLEAR_ERRORS = "CLEAR_ERRORS",

  // Set Address Checkout
  START_SET_ADDRESS_CHECKOUT = "START_SET_ADDRESS_CHECKOUT",
  SUCCESS_SET_ADDRESS_CHECKOUT = "SUCCESS_SET_ADDRESS_CHECKOUT",
  FAILURE_SET_ADDRESS_CHECKOUT = "FAILURE_SET_ADDRESS_CHECKOUT",

  // Get Carriers
  START_GET_CARRIERS = "START_GET_CARRIERS",
  SUCCESS_GET_CARRIERS = "SUCCESS_GET_CARRIERS",
  FAILURE_GET_CARRIERS = "FAILURE_GET_CARRIERS",

  // Get Payments
  START_GET_PAYMENTS = "START_GET_PAYMENTS",
  SUCCESS_GET_PAYMENTS = "SUCCESS_GET_PAYMENTS",
  FAILURE_GET_PAYMENTS = "FAILURE_GET_PAYMENTS",

  // Set Carrier Checkout
  START_SET_CARRIER_CHECKOUT = "START_SET_CARRIER_CHECKOUT",
  SUCCESS_SET_CARRIER_CHECKOUT = "SUCCESS_SET_CARRIER_CHECKOUT",
  FAILURE_SET_CARRIER_CHECKOUT = "FAILURE_SET_CARRIER_CHECKOUT",

  // Create Order
  START_CREATE_ORDER = "START_CREATE_ORDER",
  SUCCESS_CREATE_ORDER = "SUCCESS_CREATE_ORDER",
  FAILURE_CREATE_ORDER = "FAILURE_CREATE_ORDER",

  // Set Pickup Point
  START_SET_PICKUP_POINT = "START_SET_PICKUP_POINT",
  SUCCESS_SET_PICKUP_POINT = "SUCCESS_SET_PICKUP_POINT",
  FAILURE_SET_PICKUP_POINT = "FAILURE_SET_PICKUP_POINT",
}

export interface CreateOrderRequest {
  addDiscount?: number;
  discountName?: string;
  lineItems: {
    productId: number;
    idProductAttribute: number;
    quantity: number;
  }[];
  idShippingAddress: number;
  idBillingAddress: number;
  shippingLines: {
    carrierId: number;
    title: string;
  };
  note?: string;
}

export type CartState = {
  cart: Cart | null;
  loadingCart: boolean;
  loadedCart: boolean;
  errorCart: string | null;

  // Update Cart
  loadingUpdateCart: boolean;
  errorUpdateCart: string | null;
  openUpdateCartModal: boolean;
  addedProduct: any;
  loadingProductAttributes: {
    [key: number]: boolean;
  };

  // Modify voucher
  loadingModifyVoucher: boolean;
  errorModifyVoucher: string | null;

  // Set Address Checkout
  loadingSetAddressCheckout: boolean;
  errorSetAddressCheckout: string | null;
  idAddressInvoice: number | null;
  idAddressDelivery: number | null;

  // Get Carriers
  loadingGetCarriers: boolean;
  errorGetCarriers: string | null;
  carriers: Carrier[];

  // Get Payments
  loadingGetPayments: boolean;
  errorGetPayments: string | null;
  payments: any[];

  // Set Carrier Checkout
  idCarrier: number | null;
  loadingSetCarrierCheckout: boolean;
  errorSetCarrierCheckout: string | null;

  // Create Order
  loadingCreateOrder: boolean;
  errorCreateOrder: string | null;

  // Set Pickup Point
  loadingSetPickupPoint: boolean;
  errorSetPickupPoint: string | null;
};

const CartStateContext = createContext<CartState | undefined>(undefined);

type CartAction =
  // Carts
  | { type: CartActionType.START_CART }
  | {
      type: CartActionType.SUCCESS_CART;
      cart: Cart | null;
    }
  | { type: CartActionType.FAILURE_CART; error: string }

  // Update Cart
  | { type: CartActionType.START_UPDATE_CART; idProductAttribute: number }
  | {
      type: CartActionType.SUCCESS_UPDATE_CART;
      cart: Cart | null;
      addedProduct: any;
      idProductAttribute: number;
    }
  | {
      type: CartActionType.FAILURE_UPDATE_CART;
      error: string;
      idProductAttribute: number;
    }
  | { type: CartActionType.TOGGLE_UPDATE_CART_MODAL }

  // Modify voucher
  | { type: CartActionType.START_MODIFY_VOUCHER }
  | {
      type: CartActionType.SUCCESS_MODIFY_VOUCHER;
      cart: Cart | null;
      error: string | null;
    }
  | { type: CartActionType.FAILURE_MODIFY_VOUCHER; error: string }

  // Clear errors
  | { type: CartActionType.CLEAR_ERRORS }

  // Set Address Checkout
  | { type: CartActionType.START_SET_ADDRESS_CHECKOUT }
  | {
      type: CartActionType.SUCCESS_SET_ADDRESS_CHECKOUT;
      idAddressInvoice: number;
      idAddressDelivery: number;
    }
  | { type: CartActionType.FAILURE_SET_ADDRESS_CHECKOUT; error: string }

  // Get Carriers
  | { type: CartActionType.START_GET_CARRIERS }
  | {
      type: CartActionType.SUCCESS_GET_CARRIERS;
      carriers: any[];
    }
  | { type: CartActionType.FAILURE_GET_CARRIERS; error: string }

  // Get Payments
  | { type: CartActionType.START_GET_PAYMENTS }
  | {
      type: CartActionType.SUCCESS_GET_PAYMENTS;
      payments: any[];
    }
  | { type: CartActionType.FAILURE_GET_PAYMENTS; error: string }

  // Set Carrier Checkout
  | { type: CartActionType.START_SET_CARRIER_CHECKOUT }
  | {
      type: CartActionType.SUCCESS_SET_CARRIER_CHECKOUT;
      cart: Cart;
      idCarrier: number;
    }
  | { type: CartActionType.FAILURE_SET_CARRIER_CHECKOUT; error: string }

  // Create Order
  | { type: CartActionType.START_CREATE_ORDER }
  | { type: CartActionType.SUCCESS_CREATE_ORDER }
  | { type: CartActionType.FAILURE_CREATE_ORDER; error: string }

  // Set Pickup Point
  | { type: CartActionType.START_SET_PICKUP_POINT }
  | { type: CartActionType.SUCCESS_SET_PICKUP_POINT }
  | { type: CartActionType.FAILURE_SET_PICKUP_POINT; error: string };

type CartDispatch = Dispatch<CartAction>;

const CartDispatchContext = createContext<CartDispatch | undefined>(undefined);

const initialCartState: CartState = {
  // Cart
  cart: null,
  loadingCart: false,
  loadedCart: false,
  errorCart: null,

  // Update Cart
  loadingUpdateCart: false,
  errorUpdateCart: null,
  openUpdateCartModal: false,
  addedProduct: null,
  loadingProductAttributes: {},

  // Modify voucher
  loadingModifyVoucher: false,
  errorModifyVoucher: null,

  // Set Address Checkout
  loadingSetAddressCheckout: false,
  errorSetAddressCheckout: null,
  idAddressInvoice: null,
  idAddressDelivery: null,

  // Get Carriers
  loadingGetCarriers: false,
  errorGetCarriers: null,
  carriers: [],

  // Get Payments
  loadingGetPayments: false,
  errorGetPayments: null,
  payments: [],

  // Set Carrier Checkout
  idCarrier: null,
  loadingSetCarrierCheckout: false,
  errorSetCarrierCheckout: null,

  // Create Order
  loadingCreateOrder: false,
  errorCreateOrder: null,

  // Set Pickup Point
  loadingSetPickupPoint: false,
  errorSetPickupPoint: null,
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    // Carts
    case CartActionType.START_CART:
      return {
        ...state,
        loadingCart: true,
        loadedCart: false,
      };
    case CartActionType.SUCCESS_CART:
      return {
        ...state,
        cart: action.cart,
        loadingCart: false,
        loadedCart: true,
        errorCart: null,
      };
    case CartActionType.FAILURE_CART:
      return {
        ...state,
        loadingCart: false,
        loadedCart: true,
        errorCart: action.error,
      };

    // Update Cart
    case CartActionType.START_UPDATE_CART:
      return {
        ...state,
        loadingUpdateCart: true,
        loadingProductAttributes: {
          ...state.loadingProductAttributes,
          [action.idProductAttribute]: true,
        },
      };
    case CartActionType.SUCCESS_UPDATE_CART:
      return {
        ...state,
        cart: action.cart,
        loadingUpdateCart: false,
        errorUpdateCart: null,
        addedProduct: action.addedProduct,
        loadingProductAttributes: {
          ...state.loadingProductAttributes,
          [action.idProductAttribute]: false,
        },
      };
    case CartActionType.FAILURE_UPDATE_CART:
      return {
        ...state,
        loadingUpdateCart: false,
        errorUpdateCart: action.error,
        loadingProductAttributes: {
          ...state.loadingProductAttributes,
          [action.idProductAttribute]: false,
        },
      };
    case CartActionType.TOGGLE_UPDATE_CART_MODAL:
      return {
        ...state,
        openUpdateCartModal: !state.openUpdateCartModal,
        addedProduct:
          state.openUpdateCartModal === true ? null : state.addedProduct,
      };

    // Modify voucher
    case CartActionType.START_MODIFY_VOUCHER:
      return {
        ...state,
        loadingModifyVoucher: true,
      };
    case CartActionType.SUCCESS_MODIFY_VOUCHER:
      return {
        ...state,
        cart: action.cart,
        loadingModifyVoucher: false,
        errorModifyVoucher: action.error,
      };
    case CartActionType.FAILURE_MODIFY_VOUCHER:
      return {
        ...state,
        loadingModifyVoucher: false,
        errorModifyVoucher: action.error,
      };

    // Clear errors
    case CartActionType.CLEAR_ERRORS:
      return {
        ...state,
        errorCart: null,
        cart: state.cart
          ? {
              ...state.cart,
              errors: [],
            }
          : null,
      };

    // Set Address Checkout
    case CartActionType.START_SET_ADDRESS_CHECKOUT:
      return {
        ...state,
        loadingSetAddressCheckout: true,
      };
    case CartActionType.SUCCESS_SET_ADDRESS_CHECKOUT:
      return {
        ...state,
        loadingSetAddressCheckout: false,
        errorSetAddressCheckout: null,
        idAddressInvoice: action.idAddressInvoice,
        idAddressDelivery: action.idAddressDelivery,
      };
    case CartActionType.FAILURE_SET_ADDRESS_CHECKOUT:
      return {
        ...state,
        loadingSetAddressCheckout: false,
        errorSetAddressCheckout: action.error,
      };

    // Get Carriers
    case CartActionType.START_GET_CARRIERS:
      return {
        ...state,
        loadingGetCarriers: true,
      };
    case CartActionType.SUCCESS_GET_CARRIERS:
      return {
        ...state,
        loadingGetCarriers: false,
        errorGetCarriers: null,
        carriers: action.carriers,
      };
    case CartActionType.FAILURE_GET_CARRIERS:
      return {
        ...state,
        loadingGetCarriers: false,
        errorGetCarriers: action.error,
      };

    // Get Payments
    case CartActionType.START_GET_PAYMENTS:
      return {
        ...state,
        loadingGetPayments: true,
      };
    case CartActionType.SUCCESS_GET_PAYMENTS:
      return {
        ...state,
        loadingGetPayments: false,
        errorGetPayments: null,
        payments: action.payments,
      };
    case CartActionType.FAILURE_GET_PAYMENTS:
      return {
        ...state,
        loadingGetPayments: false,
        errorGetPayments: action.error,
      };

    // Set Carrier Checkout
    case CartActionType.START_SET_CARRIER_CHECKOUT:
      return {
        ...state,
        loadingSetCarrierCheckout: true,
      };

    case CartActionType.SUCCESS_SET_CARRIER_CHECKOUT: {
      let newCart = state.cart ? { ...state.cart } : null;

      if (newCart) {
        newCart = action.cart as Cart;
      }

      return {
        ...state,
        cart: newCart,
        idCarrier: action.idCarrier,
        loadingSetCarrierCheckout: false,
        errorSetCarrierCheckout: null,
      };
    }

    case CartActionType.FAILURE_SET_CARRIER_CHECKOUT:
      return {
        ...state,
        loadingSetCarrierCheckout: false,
        errorSetCarrierCheckout: action.error,
      };

    // Create Order
    case CartActionType.START_CREATE_ORDER:
      return {
        ...state,
        loadingCreateOrder: true,
      };
    case CartActionType.SUCCESS_CREATE_ORDER:
      return {
        ...state,
        loadingCreateOrder: false,
        errorCreateOrder: null,
      };
    case CartActionType.FAILURE_CREATE_ORDER:
      return {
        ...state,
        loadingCreateOrder: false,
        errorCreateOrder: action.error,
      };

    // Set Pickup Point
    case CartActionType.START_SET_PICKUP_POINT:
      return {
        ...state,
        loadingSetPickupPoint: true,
      };
    case CartActionType.SUCCESS_SET_PICKUP_POINT:
      return {
        ...state,
        loadingSetPickupPoint: false,
        errorSetPickupPoint: null,
      };
    case CartActionType.FAILURE_SET_PICKUP_POINT:
      return {
        ...state,
        loadingSetPickupPoint: false,
        errorSetPickupPoint: action.error,
      };

    default:
      throw new Error("Unhandled action");
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, initialCartState);

  return (
    <CartDispatchContext.Provider value={dispatch}>
      <CartStateContext.Provider value={useMemo(() => state, [state])}>
        {children}
      </CartStateContext.Provider>
    </CartDispatchContext.Provider>
  );
}

export function useCartState() {
  const state = useContext(CartStateContext);
  if (!state) throw new Error("CartStateContext not found");
  return state;
}

export function useCartDispatch() {
  const dispatch = useContext(CartDispatchContext);
  if (!dispatch) throw new Error("CartDispatchContext not found");
  return dispatch;
}

const getCart = async (dispatch: CartDispatch) => {
  dispatch({ type: CartActionType.START_CART });

  const response = await axiosInstance.get(`/rest${CartAPI}`, {
    params: {
      image_size: "medium_default",
    },
  });
  if (response.data.psdata) {
    const cart: Cart = snakeToCamelCaseObject(response.data.psdata);

    setTimeout(() => {
      dispatch({
        type: CartActionType.SUCCESS_CART,
        cart,
      });
    }, 1000);
  } else {
    dispatch({
      type: CartActionType.FAILURE_CART,
      error: response.data.error,
    });
  }
};

const updateCart = async (
  idProduct: number,
  idProductAttribute: number,
  qty: number,
  op: "up" | "down",
  dispatch: CartDispatch,
) => {
  dispatch({
    type: CartActionType.START_UPDATE_CART,
    idProductAttribute,
  });

  const response = await axiosInstance.get(`/rest${CartAPI}`, {
    params: {
      id_product: idProduct,
      id_product_attribute: idProductAttribute,
      qty: qty,
      update: 1,
      op: op,
      action: "update",
      image_size: "medium_default",
    },
  });

  if (response.data.psdata) {
    const cart: Cart = snakeToCamelCaseObject(response.data.psdata);

    setTimeout(() => {
      dispatch({
        type: CartActionType.SUCCESS_UPDATE_CART,
        cart,
        addedProduct: cart.products?.find(
          (product: any) =>
            Number(product.idProductAttribute) === idProductAttribute,
        ),
        idProductAttribute,
      });
      dispatch({
        type: CartActionType.TOGGLE_UPDATE_CART_MODAL,
      });
    }, 1000);
  } else {
    dispatch({
      type: CartActionType.FAILURE_UPDATE_CART,
      error: response.data.error,
      idProductAttribute,
    });
  }
};

export const deleteProductFromCart = async (
  idProduct: number,
  idProductAttribute: number,
  dispatch: CartDispatch,
) => {
  dispatch({
    type: CartActionType.START_UPDATE_CART,
    idProductAttribute,
  });

  const response = await axiosInstance.get(`/rest${CartAPI}`, {
    params: {
      id_product: idProduct,
      id_product_attribute: idProductAttribute,
      delete: 1,
      action: "update",
      image_size: "medium_default",
    },
  });

  if (response.data.psdata) {
    const cart: Cart = snakeToCamelCaseObject(response.data.psdata);

    setTimeout(() => {
      dispatch({
        type: CartActionType.SUCCESS_UPDATE_CART,
        cart,
        addedProduct: null,
        idProductAttribute,
      });
    }, 1000);
  } else {
    dispatch({
      type: CartActionType.FAILURE_UPDATE_CART,
      error: response.data.error,
      idProductAttribute,
    });
  }
};

export const addVoucherToCart = async (
  voucherCode: string,
  op: "add" | "delete",
  dispatch: CartDispatch,
  idCartRule?: number,
) => {
  dispatch({ type: CartActionType.START_MODIFY_VOUCHER });

  const response = await axiosInstance.get(`/rest${CartAPI}`, {
    params: {
      action: "update",
      discount_name: voucherCode,
      ...(op === "add" ? { addDiscount: 1 } : { deleteDiscount: idCartRule }),
    },
  });

  if (response.data.psdata) {
    const cart: Cart = snakeToCamelCaseObject(response.data.psdata);
    const responseDataErrors = response.data.psdata.errors;

    setTimeout(() => {
      dispatch({
        type: CartActionType.SUCCESS_MODIFY_VOUCHER,
        cart,
        error: responseDataErrors.length > 0 ? responseDataErrors[0] : null,
      });
    }, 1000);
  } else {
    dispatch({
      type: CartActionType.FAILURE_UPDATE_CART,
      error: response.data.error,
      idProductAttribute: 0,
    });
  }
};

const setAddressCheckout = async (
  idAddressInvoice: number,
  idAddressDelivery: number,
  dispatch: CartDispatch,
) => {
  dispatch({
    type: CartActionType.START_SET_ADDRESS_CHECKOUT,
  });

  const response = await axiosInstance.post(`/rest${SetAddressCheckoutAPI}`, {
    id_address_invoice: idAddressInvoice,
    id_address_delivery: idAddressDelivery,
  });

  if (response.data.code === 200) {
    setTimeout(() => {
      dispatch({
        type: CartActionType.SUCCESS_SET_ADDRESS_CHECKOUT,
        idAddressInvoice,
        idAddressDelivery,
      });
    }, 1000);
  } else {
    dispatch({
      type: CartActionType.FAILURE_SET_ADDRESS_CHECKOUT,
      error: response.data.error,
    });
  }
};

const getCarriers = async (dispatch: CartDispatch) => {
  dispatch({ type: CartActionType.START_GET_CARRIERS });

  const response = await axiosInstance.get(`/rest${CarriersAPI}`);

  if (response.data.code === 200 && response.data.psdata) {
    const carriers: Carrier[] = Object.values(response.data.psdata).map(
      (carrier: any) => snakeToCamelCaseObject(carrier),
    );
    dispatch({
      type: CartActionType.SUCCESS_GET_CARRIERS,
      carriers,
    });
  } else {
    dispatch({
      type: CartActionType.FAILURE_GET_CARRIERS,
      error: response.data.error,
    });
  }
};

const getPayments = async (dispatch: CartDispatch) => {
  dispatch({ type: CartActionType.START_GET_PAYMENTS });

  // Opóźnienie w celu odczekania na aktualizację koszyka po stronie PrestaShop
  setTimeout(async () => {
    const response = await axiosInstance.get(`/rest${PaymentsAPI}`);

    if (response.data.code === 200 && response.data.psdata) {
      // Spłaszcz wszystkie opcje płatności z różnych modułów w jedną tablicę
      const payments: PaymentOption[] = Object.values(response.data.psdata)
        .flat() // Spłaszcza tablice z każdego modułu
        .map((payment: any) => snakeToCamelCaseObject(payment));

      dispatch({
        type: CartActionType.SUCCESS_GET_PAYMENTS,
        payments,
      });
    } else {
      dispatch({
        type: CartActionType.FAILURE_GET_PAYMENTS,
        error: response.data.error,
      });
    }
  }, 1000);
};

const setCarrierCheckout = async (
  idAddressDelivery: number,
  idCarrier: string,
  dispatch: CartDispatch,
) => {
  dispatch({ type: CartActionType.START_SET_CARRIER_CHECKOUT });

  const response = await axiosInstance.post(`/rest${SetCarrierCheckoutAPI}`, {
    id_address: `${idAddressDelivery},`,
    id_carrier: `${idCarrier},`,
  });

  if (response.data.code === 200) {
    const responseCart = await axiosInstance.get(`/rest${CartAPI}`);

    if (responseCart.data.code === 200 && responseCart.data.psdata) {
      const cart: Cart = snakeToCamelCaseObject(responseCart.data.psdata);

      dispatch({
        type: CartActionType.SUCCESS_SET_CARRIER_CHECKOUT,
        cart,
        idCarrier: Number(idCarrier),
      });

      return {
        code: 200,
        subtotals: cart.subtotals,
      };
    } else {
      dispatch({
        type: CartActionType.FAILURE_SET_CARRIER_CHECKOUT,
        error: responseCart.data.error,
      });

      return {
        code: 400,
        error: responseCart.data.error,
      };
    }
  } else {
    dispatch({
      type: CartActionType.FAILURE_SET_CARRIER_CHECKOUT,
      error: response.data.error,
    });

    return {
      code: 400,
      error: response.data.error,
    };
  }
};

const createOrder = async (
  createOrderRequest: CreateOrderRequest,
  dispatch: CartDispatch,
) => {
  dispatch({ type: CartActionType.START_CREATE_ORDER });

  const response = await axiosInstance.post(
    `/rest${CreateOrderAPI}`,
    camelToSnakeCaseObject(createOrderRequest),
  );

  if (response.data.code === 200) {
    dispatch({ type: CartActionType.SUCCESS_CREATE_ORDER });

    console.log(response.data);

    return response.data;
  } else {
    dispatch({
      type: CartActionType.FAILURE_CREATE_ORDER,
      error: response.data.error,
    });
  }
};

const setPickupPoint = async (
  idCart: number,
  provider: "inpost" | "gls",
  pickupPoint: string,
  dispatch: CartDispatch,
) => {
  dispatch({ type: CartActionType.START_SET_PICKUP_POINT });

  const pickupPointData = (provider: "inpost" | "gls", pickupPoint: string) => {
    switch (provider) {
      case "inpost":
        return {
          id_cart: idCart,
          provider: "inpost",
          machine: pickupPoint,
        };
      case "gls":
        return {
          id_cart: idCart,
          provider: "gls",
          id_point: pickupPoint,
        };
      default:
        return {
          id_cart: idCart,
        };
    }
  };

  const response = await axiosInstance.post(
    `/rest${PickupPointsAPI}`,
    pickupPointData(provider, pickupPoint),
  );

  if (response.data.code === 200) {
    dispatch({ type: CartActionType.SUCCESS_SET_PICKUP_POINT });
  } else {
    dispatch({
      type: CartActionType.FAILURE_SET_PICKUP_POINT,
      error: response.data.error,
    });
  }
};

export function useCart() {
  const state = useCartState();
  const dispatch = useCartDispatch();

  const getCartFunc = () => getCart(dispatch);
  const updateCartFunc = (
    idProduct: number,
    idProductAttribute: number,
    qty: number,
    op: "up" | "down",
  ) => updateCart(idProduct, idProductAttribute, qty, op, dispatch);

  const toggleUpdateCartModalFunc = () =>
    dispatch({ type: CartActionType.TOGGLE_UPDATE_CART_MODAL });

  const deleteProductFromCartFunc = (
    idProduct: number,
    idProductAttribute: number,
  ) => deleteProductFromCart(idProduct, idProductAttribute, dispatch);

  const addVoucherToCartFunc = (
    voucherCode: string,
    op: "add" | "delete",
    idCartRule?: number,
  ) => addVoucherToCart(voucherCode, op, dispatch, idCartRule);

  const clearErrorsFunc = () => dispatch({ type: CartActionType.CLEAR_ERRORS });

  const setAddressCheckoutFunc = (
    idAddressInvoice: number,
    idAddressDelivery: number,
  ) => setAddressCheckout(idAddressInvoice, idAddressDelivery, dispatch);

  const getCarriersFunc = () => getCarriers(dispatch);

  const getPaymentsFunc = () => getPayments(dispatch);

  const setCarrierCheckoutFunc = async (idCarrier: string) =>
    setCarrierCheckout(state.idAddressDelivery ?? 0, idCarrier, dispatch);

  const createOrderFunc = (createOrderRequest: CreateOrderRequest) =>
    createOrder(createOrderRequest, dispatch);

  const setPickupPointFunc = (
    idCart: number,
    provider: "inpost" | "gls",
    pickupPoint: string,
  ) => setPickupPoint(idCart, provider, pickupPoint, dispatch);

  return {
    state,
    getCart: getCartFunc,
    updateCart: updateCartFunc,
    toggleUpdateCartModal: toggleUpdateCartModalFunc,
    deleteProductFromCart: deleteProductFromCartFunc,
    addVoucherToCart: addVoucherToCartFunc,
    clearErrors: clearErrorsFunc,
    setAddressCheckout: setAddressCheckoutFunc,
    getCarriers: getCarriersFunc,
    getPayments: getPaymentsFunc,
    setCarrierCheckout: setCarrierCheckoutFunc,
    createOrder: createOrderFunc,
    setPickupPoint: setPickupPointFunc,
  };
}
